<template>
	<div class="intro-form">
		<div class="form-row">
			<div class="form-row__item position-relative">
				<ValidationProvider name="title" rules="required" v-slot="{ errors }" persist>
					<input type="text"
						v-model="title"
						class="tutorial-title-input title-input title-input--full"
						placeholder="Tutorial title"
						spellcheck="true"
						:maxlength="titleMaxLength"
						v-on:blur="titleBlur"
						v-bind:class="{error : errors[0]}">

					<persistent-tooltip :content="tooltips.title.content"
						@remove="nextTip()"
						v-if="tipsShown && tooltips.title.active"
						class="title-tooltip">
					</persistent-tooltip>

					<div v-show="errors[0]" class="input-error">
						Your tutorial needs an awesome title!
					</div>
				</ValidationProvider>
			</div>
		</div>

		<p class="author"><span>By</span> {{ tutorial.member_name }}</p>
		<div class="form-row">
			<div class="form-row__item position-relative">
				<ValidationProvider name="description" rules="required" v-slot="{ errors }" persist>
					<label class="form-row__label is-required">Description</label>
					<textarea class="description-input"
						v-model="description"
						v-bind:class="{error : errors[0]}"
						placeholder="Short description for tutorial list to help others find your tutorial"
						spellcheck="true"
						:maxlength="descriptionMaxLength"
						v-on:blur="descriptionBlur">
					</textarea>

					<persistent-tooltip :content="tooltips.description.content"
						@remove="nextTip()"
						placement="bottom-start"
						v-if="tipsShown && tooltips.description.active"
						class="description-tooltip">
					</persistent-tooltip>

					<div v-show="errors[0]" class="input-error">
						Please provide a short description
					</div>
				</ValidationProvider>
			</div>
		</div>

		<div class="form-row">
			<div class="form-row__item">
				<ValidationProvider name="content_category" rules="required" v-slot="{ errors }" persist>
					<label class="form-row__label is-required">Category</label>
					<multiselect label="name"
						v-model="content_category"
						track-by="value"
						:searchable="false"
						:show-labels="false"
						:allow-empty="false"
						placeholder="Select.."
						:options="categoryOptions"
						v-bind:class="{error: errors[0]}">

						<template slot="option" scope="props">
							<span :class="`level-${props.option.level}`">
								{{ props.option.name }}
							</span>
						</template>
					</multiselect>
					<div v-show="errors[0]" class="input-error">
						Category is required
					</div>
				</ValidationProvider>
			</div>

			<div class="form-row__item">
				<ValidationProvider name="skill_level" rules="required" v-slot="{ errors }" persist>
					<label class="form-row__label">
						Skill level <span v-tooltip="tooltips.skillLevel.content" class="tooltip__opener fas fa-question-circle"></span>
					</label>

					<multiselect label="name"
						track-by="value"
						v-model="skill_level"
						:searchable="false"
						:show-labels="false"
						:allow-empty="false"
						placeholder="Select.."
						:options="skillOptions"
						v-bind:class="{error: errors[0]}">

						<template slot="option" scope="props">
							<span>
								<i :class="`gc-icon gc-icon-skill-${props.option.value}`" />{{ props.option.name }}
							</span>
						</template>
					</multiselect>

					<div class="input-error" v-show="errors[0]">
						Skill level is required
					</div>
				</ValidationProvider>
			</div>
		</div>

		<div class="form-row" v-if="isDesignCategory">
			<div class="form-row__item">
				<ValidationProvider name="system" rules="required" v-slot="{ errors }" persist>
					<label class="form-row__label is-required">Software</label>
					<multiselect label="name"
						v-model="system"
						track-by="value"
						:searchable="false"
						:show-labels="false"
						:allow-empty="false"
						placeholder="Select.."
						:options="softwareOptions"
						v-bind:class="{error: errors[0]}">
					</multiselect>

					<div class="input-error" v-show="errors[0]">
						Software is required
					</div>
				</ValidationProvider>
			</div>
		</div>

		<div class="form-row">
			<div class="form-row__item">
				<label class="form-row__label">
					Tags <span class="form-row__label--help">(up to 8, separate with commas)</span>
				</label>
				<TagInput placeholder="Add tags.." :tags="tutorial.tags" spellcheck="true"/>
			</div>
		</div>

		<div class="form-row" v-if="showSeoFields">
			<div class="form-row__item">
				<label for="seo-title-text" class="form-row__label">SEO Title</label>
				<div>
					<input id="seo-title-text" type="text"
						class="seo-title-input"
						v-model="seo_title"
						:maxlength="titleMaxLength"
						placeholder="Title of the web page">
				</div>
			</div>
		</div>

		<div class="form-row" v-if="showSeoFields">
			<div class="form-row__item">
				<label for="seo-meta-description-textarea" class="form-row__label">Meta Description</label>

				<textarea id="seo-meta-description-textarea"
					class="description-input"
					v-model="seo_description"
					:maxlength="descriptionMaxLength"
					placeholder="Meta description is used by search engines to display the page in search results">
				</textarea>
			</div>
		</div>

		<div class="form-row">
			<div class="form-row__item">
				<ValidationProvider name="preview_picture" rules="required" v-slot="{ errors }" persist>

					<label class="form-row__label is-required">Tutorial thumbnail</label>

					<Thumbnail
						v-bind:class="{error: errors[0]}"
						v-model="preview_picture">
					</Thumbnail>

					<div class="input-error" v-show="errors[0]">
						Thumbnail is required
					</div>
				</ValidationProvider>
			</div>
		</div>

	</div>
</template>

<script>
	import TagInput from './TagInput'
	import Thumbnail from './Thumbnail'
	import Multiselect from 'vue-multiselect'
	import PersistentTooltip from './PersistentTooltip'
	import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'

	export default {
		name: 'intro-form',
		components: { TagInput, Multiselect, PersistentTooltip, Thumbnail },
		methods: {
			...mapMutations([
				'nextTip',
				'setIntroError',
				'activateIntro'
			]),
			...mapActions([
				'publishTutorial',
				'setStepDetailsFlash',
				'activateStepByIndex'
			]),
			validationSuccess () {
				if (this.tutorial.steps[0].content) {
					this.publishTutorial()
				} else {
					this.setStepDetailsFlash()
					this.activateStepByIndex(0)
				}
			},
			validationError () {
				this.setIntroError(true)
				this.activateIntro()
			},
			titleBlur () {
				if (this.tooltips.title.active) {
					this.nextTip()
				}
			},
			descriptionBlur () {
				if (this.tooltips.description.active) {
					this.nextTip()
				}
			},
			handleAddStepTip () {
				if (this.tooltips.addStep.active) {
					this.nextTip()
				}
			}
		},
		data: function () {
			return {
				descriptionMaxLength: Grabcad.AttributeLimits.Generic.CONTENT_DESCRIPTION_LONG,
				titleMaxLength: Grabcad.AttributeLimits.Generic.TITLE,
			}
		},
		computed: {
			...mapState({
				tutorial: state => state.tutorial,
				tooltips: state => state.tooltips,
				memberName: state => state.memberName,
				showSeoFields: state => state.showSeoFields,
				activeStepIndex: state => state.activeStepIndex,
				skillOptions: state => state.options.skillOptions,
				categoryOptions: state => state.options.categoryOptions,
				softwareOptions: state => state.options.softwareOptions
			}),
			...mapGetters(['isDesignCategory', 'tipsShown']),
			title: {
				get () { return this.tutorial.title },
				set (value) { this.$store.commit('updateTitle', value) }
			},
			seo_title: {
				get () { return this.tutorial.seo_title },
				set (value) { this.$store.commit('updateSeoTitle', value) }
			},
			description: {
				get () { return this.tutorial.description },
				set (value) { this.$store.commit('updateDescription', value) }
			},
			seo_description: {
				get () { return this.tutorial.seo_description },
				set (value) { this.$store.commit('updateSeoDescription', value) }
			},
			content_category: {
				get () {
					return this.categoryOptions.find((option) => {
						return option.value === this.tutorial.content_category_id
					})
				},
				set (selected) {
					this.$store.commit('updateCategory', selected.value)
				}
			},
			system: {
				get () {
					return this.softwareOptions.find((option) => {
						return option.value === this.tutorial.system_id
					})
				},
				set (selected) {
					this.$store.commit('updateSystem', selected.value)
				}
			},
			skill_level: {
				get () {
					return this.skillOptions.find((option) => {
						return option.value === this.tutorial.skill_level
					})
				},
				set (selected) {
					this.$store.commit('updateSkillLevel', selected.value)
				}
			},
			preview_picture: {
				get () {
					return this.tutorial.preview_picture.id
				}
			}
		},
		created () {
			this.$eventHub.$on('validationSuccess', result => result ? this.validationSuccess(result) : this.validationError(result));
		},
		watch: {
			'errors.errors' (value) {
				if (value.length === 0) {
					this.setIntroError(false)
				}
			},
			'title' () {
				if (this.tooltips.title.active && this.tutorial.title.length) {
					this.nextTip()
				}
			},
			'description' () {
				if (this.tooltips.description.active && this.tutorial.description.length) {
					this.nextTip()
				}
			},
			activeStepIndex () {
				this.handleAddStepTip()
			}
		}
	}
</script>

<style lang="scss">
	@import "@/styles/tutorial_editor/globals";

	.title-tooltip, .description-tooltip {
		position: absolute;
	}

	@include breakpoint--desktop-only {
		.title-tooltip {
			top: 50%;
		}

		.description-tooltip, .title-tooltip {
			right:50%;
		}

		.description-tooltip {
			top: 54px;
		}
	}

	.title-input {
		padding: 2px 0 0;
		&.error { padding: 2px 2px 0 5px; }
	}
</style>
